/**
 * <sky-cart-drawer> — Sky cart drawer.
 *
 * - Opens from any `[data-sky-cart-open]` element, closes on backdrop / close button / Escape.
 * - Cart logic (lines, deals, note, shipping, discounts, re-rendering) comes from `SkyCartLines`.
 * - Auto-opens after an add to cart, except on the cart page (the page updates in place).
 * - "Recommended" panel: products related to the first cart item (fallback: a collection), each with an
 *   expandable variant picker; adding goes through sky-cart.js (`form[data-sky-product-form]`).
 */
import { SkyCartLines } from '@theme/sky-cart-lines';

class SkyCartDrawer extends SkyCartLines {
  connectedCallback() {
    super.connectedCallback();
    this.panel = this.querySelector('.sky-cd__panel');
    this.rec = /** @type {HTMLElement | null} */ (this.querySelector('[data-rec]'));

    this.onDocumentClick = (event) => {
      const opener = event.target instanceof Element && event.target.closest('[data-sky-cart-open]');
      if (!opener) return;
      event.preventDefault();
      this.opener = /** @type {HTMLElement} */ (opener);
      this.open();
    };
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    };

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.unlock();
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  /** @param {any} event */
  onExternalUpdate(event) {
    if (event.action === 'add' && !document.querySelector('sky-main-cart')) this.open();
  }

  /* ---------- Open / close ---------- */

  open() {
    if (this.isOpen) return;
    this.hidden = false;
    document.documentElement.setAttribute('scroll-lock', '');
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.panel?.focus({ preventScroll: true });
      })
    );
  }

  close() {
    this.closeRec();
    this.classList.remove('is-open');
    const done = () => {
      if (this.isOpen) return;
      this.hidden = true;
      this.unlock();
    };
    this.panel?.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 700);
    this.opener?.focus({ preventScroll: true });
  }

  unlock() {
    document.documentElement.removeAttribute('scroll-lock');
  }

  /** @param {string | undefined} html */
  renderSection(html) {
    super.renderSection(html);
    const trigger = this.querySelector('[data-rec-open]');
    if (!trigger) this.closeRec();
    else trigger.setAttribute('aria-expanded', String(this.classList.contains('is-rec-open')));
  }

  /** @param {MouseEvent} event */
  handleClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-cart-close]')) {
      const link = target.closest('a');
      if (!link || link.getAttribute('href') === window.location.pathname) event.preventDefault();
      this.close();
      return;
    }
    if (target.closest('[data-rec-open]')) {
      if (this.classList.contains('is-rec-open')) this.closeRec();
      else this.openRec();
      return;
    }
    if (target.closest('[data-rec-close]')) {
      this.closeRec();
      return;
    }
    const toggle = target.closest('[data-xc-toggle]');
    if (toggle) {
      this.toggleCard(/** @type {HTMLElement} */ (toggle.closest('[data-xc]')));
      return;
    }
    const step = target.closest('[data-xc-step]');
    if (step) {
      const input = /** @type {HTMLInputElement | null} */ (step.parentElement?.querySelector('input'));
      if (input) input.value = String(Math.max(1, (Number(input.value) || 1) + Number(step.getAttribute('data-xc-step'))));
      return;
    }
    super.handleClick(event);
  }

  /** @param {Event} event */
  handleChange(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.matches('[data-xc-option]')) {
      this.updateVariant(/** @type {HTMLElement} */ (target.closest('[data-xc]')));
      return;
    }
    super.handleChange(event);
  }

  /* ---------- Recommended panel ---------- */

  async openRec() {
    if (!this.rec) return;
    this.rec.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-rec-open')));
    this.querySelector('[data-rec-open]')?.setAttribute('aria-expanded', 'true');

    const productId = this.querySelector('[data-rec-open]')?.getAttribute('data-rec-product') || '';
    if (this.rec.dataset.loadedFor !== productId) await this.loadRec(productId);
  }

  closeRec() {
    if (!this.rec || !this.classList.contains('is-rec-open')) return;
    this.classList.remove('is-rec-open');
    this.querySelector('[data-rec-open]')?.setAttribute('aria-expanded', 'false');
    setTimeout(() => {
      if (this.rec && !this.classList.contains('is-rec-open')) this.rec.hidden = true;
    }, 500);
  }

  /** @param {string} productId */
  async loadRec(productId) {
    const rec = this.rec;
    const body = rec?.querySelector('[data-rec-body]');
    if (!rec || !body) return;
    rec.dataset.loadedFor = productId;
    body.classList.add('is-loading');

    /** @param {string} url */
    const list = async (url) => {
      const html = await (await fetch(url)).text();
      return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rec-list]');
    };

    try {
      let fresh = productId
        ? await list(`${rec.dataset.recUrl}?section_id=sky-cart-recommended&product_id=${productId}&limit=10&intent=related`)
        : null;
      if (!fresh?.children.length) fresh = await list(`${rec.dataset.fallbackUrl}?section_id=sky-cart-recommended`);
      body.replaceChildren(...(fresh ? [document.importNode(fresh, true)] : []));
    } catch {
      delete rec.dataset.loadedFor;
    } finally {
      body.classList.remove('is-loading');
    }
  }

  /** @param {HTMLElement | null} card */
  toggleCard(card) {
    if (!card) return;
    const open = !card.classList.contains('is-expanded');
    this.querySelectorAll('[data-xc].is-expanded').forEach((other) => {
      other.classList.remove('is-expanded');
      other.querySelector('[data-xc-toggle]')?.setAttribute('aria-expanded', 'false');
    });
    card.classList.toggle('is-expanded', open);
    card.querySelector('[data-xc-toggle]')?.setAttribute('aria-expanded', String(open));
  }

  /** Pick the variant matching the checked options and update id, price, image and button. */
  /** @param {HTMLElement | null} card */
  updateVariant(card) {
    if (!card) return;
    let variants = [];
    try {
      variants = JSON.parse(card.querySelector('[data-xc-variants]')?.textContent || '[]');
    } catch {}

    const selected = [...card.querySelectorAll('fieldset')].map((fieldset) => {
      const input = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'));
      const label = fieldset.querySelector('[data-xc-selected]');
      if (label && input) label.textContent = input.value;
      return input?.value;
    });
    const variant = variants.find((v) => v.options.every((value, i) => value === selected[i]));

    const id = /** @type {HTMLInputElement | null} */ (card.querySelector('[data-xc-id]'));
    const add = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-xc-add]'));
    const price = card.querySelector('[data-xc-price]');
    const image = /** @type {HTMLImageElement | null} */ (card.querySelector('.sky-xc__media img'));

    if (id) id.value = variant ? String(variant.id) : '';
    if (add) {
      const available = Boolean(variant?.available);
      add.disabled = !available;
      add.textContent = (available ? add.dataset.labelAdd : add.dataset.labelSold) || '';
    }
    if (variant && price) {
      price.innerHTML = `<span>${variant.price}</span>${variant.compare ? `<s>${variant.compare}</s>` : ''}`;
    }
    if (variant?.image && image) {
      image.removeAttribute('srcset');
      image.src = variant.image;
    }
  }
}

if (!customElements.get('sky-cart-drawer')) customElements.define('sky-cart-drawer', SkyCartDrawer);
